import { useState } from 'react';
import type { CreateModuleRequest, CurriculumModule } from '@/lib/api/trainer';

interface Props {
  initial?: CurriculumModule;
  onSave: (req: CreateModuleRequest) => Promise<void>;
  onCancel: () => void;
}

const inputClass = 'w-full rounded-md border border-[#ececec] bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring';
const labelClass = 'mb-1.5 block text-xs font-medium text-[#6b7280]';

export function ModuleForm({ initial, onSave, onCancel }: Props) {
  const [title, setTitle] = useState(initial?.title ?? '');
  const [phase, setPhase] = useState(initial?.phase ?? '');
  const [skillLabel, setSkillLabel] = useState(initial?.skill_label ?? '');
  const [busy, setBusy] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await onSave({
        title: title.trim(),
        skill_label: skillLabel.trim() || undefined,
        phase: phase.trim(),
        sort_order: 0,
      });
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-5">
      <div>
        <label className={labelClass}>Title *</label>
        <input
          required
          autoFocus
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. JavaScript Fundamentals"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>Phase *</label>
        <input
          required
          value={phase}
          onChange={(e) => setPhase(e.target.value)}
          placeholder="e.g. Fundamentals, Backend, Devops"
          className={inputClass}
        />
      </div>

      <div>
        <label className={labelClass}>
          Skill label <span className="text-[#6b7280] font-normal">(optional)</span>
        </label>
        <input
          value={skillLabel}
          onChange={(e) => setSkillLabel(e.target.value)}
          placeholder="e.g. CSS"
          className={inputClass}
        />
        <p className="mt-1 text-[11px] text-[#6b7280]">
          Short name shown on student progress page
        </p>
      </div>

      <div className="flex justify-end gap-2 pt-2">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-md border border-[#ececec] px-4 py-2 text-sm hover:bg-[#fafafa] transition-colors"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={busy || !title.trim() || !phase.trim()}
          className="rounded-md bg-[#ff5a1f] px-4 py-2 text-sm font-medium text-white disabled:opacity-50 transition-colors"
        >
          {busy ? 'Saving…' : initial ? 'Update module' : 'Add module'}
        </button>
      </div>
    </form>
  );
}
